<template>
  <div class="magic-review">
    <!-- Standalone header (no IntercomLayout / no sidebar / no login) -->
    <header class="review-header">
      <div class="header-content">
        <h1 class="header-title">{{ $t('magicReview.title') }}</h1>
      </div>
    </header>

    <main class="review-main">
      <!-- Loading State -->
      <div v-if="state === 'loading'" class="state-container">
        <div class="spinner"></div>
        <p class="state-text">{{ $t('magicReview.validating') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="state === 'error'" class="state-container error">
        <div class="error-icon">!</div>
        <h2 class="state-heading">{{ errorTitle }}</h2>
        <p class="state-text">{{ errorMessage }}</p>
      </div>

      <!-- Success State (after review submitted) -->
      <div v-else-if="state === 'success'" class="state-container success">
        <div class="success-icon">✓</div>
        <h2 class="state-heading">{{ $t('magicReview.reviewSubmitted') }}</h2>
        <p class="state-text">{{ $t('magicReview.reviewSubmittedDesc') }}</p>
      </div>

      <!-- Review State -->
      <div v-else-if="state === 'review'" class="review-content">
        <div class="review-grid">
          <!-- Left Column: Case Info + Calculation -->
          <div class="left-column">
            <!-- Case Info -->
            <IntercomCard>
              <h2 class="section-title">{{ $t('magicReview.caseInfo') }}</h2>
              <div class="info-grid">
                <div class="info-row">
                  <span class="info-label">{{ $t('case.caseNumber') }}</span>
                  <span class="info-value">{{ caseData.case?.case_number }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.deceased') }}</span>
                  <span class="info-value">{{ caseData.case?.deceased_name }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.deceasedGender') }}</span>
                  <span class="info-value">{{ caseData.case?.deceased_gender }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">{{ $t('case.school') }}</span>
                  <span class="info-value">{{ caseData.case?.school }}</span>
                </div>
                <div v-if="caseData.case?.estate_value" class="info-row">
                  <span class="info-label">{{ $t('case.estate') }}</span>
                  <span class="info-value">₹{{ formatNumber(caseData.case.estate_value) }}</span>
                </div>
                <div v-if="caseData.case?.notes" class="info-row full-width">
                  <span class="info-label">{{ $t('case.notes') }}</span>
                  <span class="info-value">{{ caseData.case.notes }}</span>
                </div>
              </div>

              <!-- Heirs List -->
              <h3 class="subsection-title">{{ $t('case.heirs') }}</h3>
              <div class="heirs-list">
                <div
                  v-for="heir in caseData.heirs"
                  :key="heir.id"
                  class="heir-item"
                  :class="{ blocked: heir.is_blocked }"
                >
                  <span class="heir-name">{{ getHeirLabel(heir.heir_category) }}</span>
                  <span class="heir-count">× {{ heir.heir_count }}</span>
                  <span v-if="heir.is_blocked" class="heir-blocked">
                    ({{ $t('result.blockedBy') }}: {{ getHeirLabel(heir.blocked_by) }})
                  </span>
                </div>
              </div>
            </IntercomCard>

            <!-- Auto-Calculated Result -->
            <IntercomCard v-if="calcResult">
              <h2 class="section-title">{{ $t('review.autoCalculation') }}</h2>
              <CalculationResult :result="calcResult" />
            </IntercomCard>
          </div>

          <!-- Right Column: Review Panel -->
          <div class="right-column">
            <IntercomCard>
              <ScholarReviewPanel
                :existing-shares="caseData.shares || []"
                :is-submitting="isSubmitting"
                @submit="handleReviewSubmit"
              />
            </IntercomCard>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useScholarReview } from '../composables/useScholarReview.js'
import { IntercomCard } from '@shared/components/intercom'
import CalculationResult from '../components/CalculationResult.vue'
import ScholarReviewPanel from '../components/ScholarReviewPanel.vue'

const { t } = useI18n()
const route = useRoute()

const state = ref('loading') // loading | review | success | error
const errorTitle = ref('')
const errorMessage = ref('')
const caseData = ref({})
const isSubmitting = ref(false)

// Extract token from URL query parameter
const token = computed(() => route.query.token || '')

// Build CalcResult format expected by CalculationResult component
const calcResult = computed(() => {
  const calc = caseData.value?.calculation
  if (!calc) return null

  return {
    shares: (calc.shares || []).map(s => ({
      heir_category: s.heir_category,
      heir_count: s.heir_count,
      share_percentage: s.share_percentage,
      per_person_pct: s.per_person_pct,
      amount_total: s.amount_total,
      amount_per_person: s.amount_per_person,
      heir_type: s.heir_type,
      rule_applied: s.rule_applied,
      is_blocked: s.is_blocked,
    })),
    awl_applied: calc.awl_applied,
    awl_denominator: calc.awl_denominator,
    radd_applied: calc.radd_applied,
    radd_surplus_pct: calc.radd_surplus_pct,
    total_estate: calc.total_estate,
    total_deductions: calc.total_deductions,
    total_distributable: calc.total_distributable,
    school: calc.school_used,
    algorithm_version: calc.algorithm_version,
    steps: calc.calculation_steps,
  }
})

const heirLabelMap = {
  husband: 'heir.husband', wife: 'heir.wife', son: 'heir.son', daughter: 'heir.daughter',
  father: 'heir.father', mother: 'heir.mother', sons_son: 'heir.sonsSon',
  sons_daughter: 'heir.sonsDaughter', fathers_father: 'heir.fathersFather',
  paternal_grandmother: 'heir.paternalGrandmother', maternal_grandmother: 'heir.maternalGrandmother',
  full_brother: 'heir.fullBrother', full_sister: 'heir.fullSister',
  paternal_half_brother: 'heir.paternalHalfBrother', paternal_half_sister: 'heir.paternalHalfSister',
  maternal_half_brother: 'heir.maternalHalfBrother', maternal_half_sister: 'heir.maternalHalfSister',
  full_brothers_son: 'heir.fullBrothersSon', paternal_half_brothers_son: 'heir.paternalHalfBrothersSon',
  full_paternal_uncle: 'heir.fullPaternalUncle', paternal_half_uncle: 'heir.paternalHalfUncle',
  full_cousin: 'heir.fullCousin', paternal_half_cousin: 'heir.paternalHalfCousin',
}

const getHeirLabel = (category) => {
  if (!category) return ''
  const key = heirLabelMap[category]
  return key ? t(key) : category.replace(/_/g, ' ')
}

const formatNumber = (num) => {
  if (!num) return '0'
  return new Intl.NumberFormat('en-IN').format(num)
}

const fetchReviewData = async () => {
  if (!token.value) {
    state.value = 'error'
    errorTitle.value = t('magicReview.errorNoToken')
    errorMessage.value = t('magicReview.errorNoTokenDesc')
    return
  }

  try {
    const { getCaseData } = useScholarReview(token.value)
    const data = await getCaseData()
    caseData.value = data
    state.value = 'review'
  } catch (err) {
    state.value = 'error'
    const status = err.response?.status
    const detail = err.response?.data?.detail || err.message

    if (status === 401) {
      errorTitle.value = t('magicReview.errorExpired')
      errorMessage.value = t('magicReview.errorExpiredDesc')
    } else if (status === 403) {
      // Could be reassigned, already reviewed, or cancelled
      errorTitle.value = t('magicReview.errorForbidden')
      errorMessage.value = detail
    } else {
      errorTitle.value = t('magicReview.errorGeneric')
      errorMessage.value = detail
    }
  }
}

const handleReviewSubmit = async (payload) => {
  isSubmitting.value = true
  try {
    const { approve, adjust, override, requestInfo, uploadDocument } = useScholarReview(token.value)

    // Step 1: Upload document if provided
    let documentKey = undefined
    if (payload.documentFile) {
      const uploadResult = await uploadDocument(payload.documentFile)
      documentKey = uploadResult.document_key
    }

    // Step 2: Submit review with document_key
    switch (payload.mode) {
      case 'approve':
        await approve({
          reasoning: payload.reasoning || undefined,
          notes: payload.notes || undefined,
          document_key: documentKey,
        })
        break
      case 'adjust':
        await adjust({
          shares: payload.shares,
          reasoning: payload.reasoning,
          notes: payload.notes || undefined,
          document_key: documentKey,
        })
        break
      case 'override':
        await override({
          shares: payload.shares,
          reasoning: payload.reasoning,
          notes: payload.notes || undefined,
          document_key: documentKey,
        })
        break
      case 'request_info':
        await requestInfo({
          questions: payload.questions,
          notes: payload.notes || undefined,
          document_key: documentKey,
        })
        break
    }

    state.value = 'success'
  } catch (err) {
    const detail = err.response?.data?.detail || err.message
    // Don't navigate away — show inline error
    errorTitle.value = t('magicReview.submitFailed')
    errorMessage.value = detail
    state.value = 'error'
  } finally {
    isSubmitting.value = false
  }
}

onMounted(() => {
  fetchReviewData()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.magic-review {
  min-height: 100vh;
  background: var(--color-gray-50);
}

/* Header */
.review-header {
  background: var(--color-white);
  border-bottom: 1px solid var(--color-gray-200);
  padding: var(--space-4) var(--space-6);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
}

.header-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

/* Main */
.review-main {
  max-width: 1200px;
  margin: 0 auto;
  padding: var(--space-6);
}

/* States */
.state-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  gap: var(--space-4);
}

.state-heading {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0;
}

.state-text {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  max-width: 400px;
  margin: 0;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-gray-200);
  border-top-color: var(--color-blue);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #fef2f2;
  color: var(--color-error);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
}

.success-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #ecfdf5;
  color: #059669;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
}

/* Review Content */
.review-grid {
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: var(--space-6);
  align-items: start;
}

@media (max-width: 900px) {
  .review-grid {
    grid-template-columns: 1fr;
  }
}

.section-title {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4) 0;
}

.subsection-title {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: var(--space-4) 0 var(--space-2) 0;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.info-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-row.full-width {
  grid-column: 1 / -1;
}

.info-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.info-value {
  font-size: var(--font-size-base);
  color: var(--color-charcoal);
}

/* Heirs List */
.heirs-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.heir-item {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  background: var(--color-gray-50);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
}

.heir-item.blocked {
  opacity: 0.5;
  text-decoration: line-through;
}

.heir-name {
  color: var(--color-charcoal);
  font-weight: var(--font-weight-medium);
}

.heir-count {
  color: var(--color-gray-500);
}

.heir-blocked {
  color: var(--color-error);
  font-size: var(--font-size-xs);
}
</style>
